import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mic, 
  Square, 
  Play, 
  Pause, 
  RotateCcw, 
  Check, 
  SkipForward,
  FastForward,
  List,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Hand,
  Home,
  Users,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Plus,
  Volume2
} from 'lucide-react';

import { useLifeStory } from '../contexts/LifeStoryContext';
import { useAudioRecorder, useAudioPlayback } from '../hooks/useAudioRecorder';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../lib/firebase';

export default function RecordingScreen() {
  const navigate = useNavigate();
  
  const {
    lifeStory,
    currentQuestion,
    responses,
    residentName,
    loading,
    error,
    hasConsent,
    storyId,
    isStandalone,
    recordConsent,
    saveResponse,
    deleteResponse,
    addRecording,
    skipQuestion,
    skipSection,
    nextQuestion,
    goToNext,
    goToPrevious,
    getProgress,
    skippedSections,
    sections
  } = useLifeStory();
  
  const {
    isRecording,
    duration,
    audioBlob,
    audioUrl,
    error: recorderError,
    permissionGranted,
    requestPermission,
    startRecording,
    stopRecording,
    cancelRecording,
    reset
  } = useAudioRecorder();
  
  const playback = useAudioPlayback();
  const existingPlayback = useAudioPlayback();
  
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isAddingMore, setIsAddingMore] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [existingAudioUrl, setExistingAudioUrl] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  
  // Swipe handling
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const containerRef = useRef(null);

  const progress = getProgress();
  
  // Get existing response for current question
  const existingResponse = currentQuestion ? responses[currentQuestion.id] : null;

  // Helper to get the base path for navigation
  const getBasePath = () => {
    return `/story/${storyId}`;
  };
  
  // Load existing audio URL when viewing an answered question
  useEffect(() => {
    if (existingResponse?.audioUrl && !isAddingMore) {
      setLoadingExisting(true);
      const audioRef = ref(storage, existingResponse.audioUrl);
      getDownloadURL(audioRef)
        .then(url => {
          setExistingAudioUrl(url);
          existingPlayback.load(url);
        })
        .catch(err => {
          console.error('Error loading existing audio:', err);
        })
        .finally(() => {
          setLoadingExisting(false);
        });
    } else {
      setExistingAudioUrl(null);
    }
  }, [existingResponse?.audioUrl, currentQuestion?.id, isAddingMore]);

  // Load audio for preview when available
  useEffect(() => {
    if (audioUrl) {
      playback.load(audioUrl);
      setShowPreview(true);
    }
  }, [audioUrl]);

  // ============================================
  // Handlers
  // ============================================
  
  const handleRecord = async () => {
    if (isRecording) {
      stopRecording();
    } else {
      setSaveError(null);
      await startRecording();
    }
  };

  const handleSave = async () => {
    if (!audioBlob || !currentQuestion) return;
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Handle consent questions specially
      if (currentQuestion.isConsent && currentQuestion.consentType === 'verbal') {
        // For verbal consent, we need to confirm they said yes
        const confirmed = window.confirm(
          'Did the resident agree to participate?'
        );
        
        if (!confirmed) {
          const usePOA = window.confirm(
            'Would you like to use POA Authorization instead?'
          );
          if (usePOA) {
            navigate(`${getBasePath()}/poa-consent`);
          }
          setIsSaving(false);
          return;
        }
      }
      
      // Save the response
      await saveResponse(
        currentQuestion.id,
        currentQuestion.section,
        audioBlob
      );
      
      // If this was consent, also record the consent object
      if (currentQuestion.isConsent && currentQuestion.consentType === 'verbal') {
        await recordConsent({
          type: 'verbal',
          verbal: true,
          publicSharingAllowed: true // Will be set by consent_2 question
        });
        
        // Advance past the consent question to the first real question
        nextQuestion();
      }
      
      // Reset for next question
      reset();
      setShowPreview(false);
      
    } catch (err) {
      console.error('Save error:', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReRecord = () => {
    playback.stop();
    reset();
    setShowPreview(false);
  };

  const handleSkip = () => {
    if (window.confirm('Skip this question? You can come back to it later.')) {
      skipQuestion();
    }
  };

  const handleSkipSection = async () => {
    const sectionName = sections[currentQuestion.section]?.title || currentQuestion.section;
    if (window.confirm(`Skip all questions in "${sectionName}"? You can come back to this section later from the home screen.`)) {
      await skipSection(currentQuestion.section);
    }
  };

  // Check if current section can be skipped entirely
  const canSkipSection = currentQuestion && ['children', 'military', 'faith'].includes(currentQuestion.section);

  const handleSaveAndExit = () => {
    // If recording, stop it first
    if (isRecording) {
      cancelRecording();
    }
    // Navigate back to home - progress is auto-saved
    navigate(-1);
  };

  // Swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    
    // Don't swipe during recording or preview
    if (isRecording || showPreview) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    
    if (diff > threshold) {
      // Swiped left - go to next
      handleNavigateNext();
    } else if (diff < -threshold) {
      // Swiped right - go to previous
      handleNavigatePrevious();
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleNavigateNext = () => {
    if (isRecording || showPreview) return;
    existingPlayback.stop();
    setIsAddingMore(false);
    goToNext();
  };

  const handleNavigatePrevious = () => {
    if (isRecording || showPreview) return;
    existingPlayback.stop();
    setIsAddingMore(false);
    goToPrevious();
  };

  const handleDelete = async () => {
    if (!currentQuestion || !existingResponse) return;
    
    if (!window.confirm('Delete this recording? This cannot be undone.')) {
      return;
    }
    
    setIsDeleting(true);
    try {
      await deleteResponse(currentQuestion.id);
      existingPlayback.stop();
      setExistingAudioUrl(null);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete recording. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddMore = () => {
    setIsAddingMore(true);
    existingPlayback.stop();
  };

  const handleSaveAdditional = async () => {
    if (!audioBlob || !currentQuestion) return;
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      await addRecording(currentQuestion.id, audioBlob);
      reset();
      setShowPreview(false);
      setIsAddingMore(false);
    } catch (err) {
      console.error('Save additional error:', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelAddMore = () => {
    reset();
    setShowPreview(false);
    setIsAddingMore(false);
  };

  // ============================================
  // Display helpers
  // ============================================

  // Get display name based on story type
  const getDisplayName = () => {
    if (lifeStory?.storyType === 'couple') {
      const name1 = lifeStory.preferredName || lifeStory.residentName;
      const name2 = lifeStory.secondPerson?.preferredName || lifeStory.secondPerson?.name;
      return `${name1} & ${name2}`;
    }
    return residentName;
  };

  // Get mode badge for header
  const getModeBadge = () => {
    const isCouple = lifeStory?.storyType === 'couple';
    const hasInterviewer = lifeStory?.hasInterviewer;
    
    if (isCouple && hasInterviewer) {
      return {
        icon: Users,
        label: 'Couple + Interviewer',
        tip: "Record all voices together - we'll identify each person in the transcript"
      };
    }
    if (hasInterviewer) {
      return {
        icon: UserPlus,
        label: 'Interview',
        tip: "Record the full conversation - we'll identify each voice in the transcript"
      };
    }
    if (isCouple) {
      return {
        icon: Users,
        label: 'Couple',
        tip: "Both voices will be captured and identified"
      };
    }
    return null;
  };

  // ============================================
  // Format duration
  // ============================================
  
  const formatDuration = (seconds) => {
    // Handle invalid values (Infinity, NaN)
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '--:--';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ============================================
  // Loading state
  // ============================================
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-quiltt-navy border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // Error state
  // ============================================
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Something went wrong</h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-quiltt-navy text-white rounded-lg hover:bg-quiltt-800"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // Consent screen (before main recording)
  // ============================================
  
  if (!hasConsent && currentQuestion?.isConsent) {
    return (
      <div className="h-[100dvh] bg-gray-50 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b px-4 py-3 flex items-center flex-shrink-0">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="ml-2 font-semibold text-gray-900">Life Story</h1>
        </header>
        
        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 pb-8 text-center safe-area-bottom">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-6">
            <Hand className="w-8 h-8 text-quiltt-navy" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Before We Begin
          </h2>
          
          <p className="text-xl text-gray-700 mb-2 max-w-md">
            {currentQuestion.question.replace('{name}', residentName)}
          </p>
          
          {currentQuestion.followUp && (
            <p className="text-gray-500 mb-8">
              {currentQuestion.followUp}
            </p>
          )}
          
          <p className="text-sm text-gray-500 mb-8 max-w-sm">
            Please record {residentName}'s verbal response. We need their consent to continue.
          </p>
          
          {/* Recording controls */}
          {showPreview ? (
            <PreviewControls
              duration={duration}
              playback={playback}
              onSave={handleSave}
              onReRecord={handleReRecord}
              isSaving={isSaving}
            />
          ) : (
            <RecordButton
              isRecording={isRecording}
              duration={duration}
              onClick={handleRecord}
              error={recorderError}
            />
          )}
          
          {saveError && (
            <p className="mt-4 text-sm text-red-600">{saveError}</p>
          )}
          
          <button
            onClick={() => navigate(`${getBasePath()}/poa-consent`)}
            className="mt-8 text-quiltt-navy hover:text-quiltt-navy text-sm underline"
          >
            {residentName} cannot provide consent? Use POA Authorization →
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // All done screen
  // ============================================
  
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <CheckCircle2 className="w-20 h-20 text-green-500 mb-6" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2">All Done!</h2>
        <p className="text-gray-600 mb-8 max-w-md">
          You've answered all the questions. {getDisplayName()}'s story is complete!
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Back to Home
          </button>
          <button
            onClick={() => navigate(`${getBasePath()}/sections`)}
            className="px-6 py-3 bg-quiltt-navy text-white rounded-lg hover:bg-quiltt-800 font-medium"
          >
            View Summary
          </button>
        </div>
      </div>
    );
  }

  const modeBadge = getModeBadge();

  // ============================================
  // Main recording interface
  // ============================================
  
  return (
    <div 
      ref={containerRef}
      className="h-[100dvh] bg-gray-50 flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <button
            onClick={handleSaveAndExit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Save progress and exit"
          >
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">Save & Exit</span>
          </button>
          
          <div className="flex-1 mx-4">
            <div className="text-xs text-gray-500 text-center mb-1">
              {progress.completionPercentage}% Complete
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-quiltt-navy transition-all duration-300"
                style={{ width: `${progress.completionPercentage}%` }}
              />
            </div>
          </div>
          
          <button
            onClick={() => navigate(`${getBasePath()}/sections`)}
            className="p-2 -mr-2 hover:bg-gray-100 rounded-lg"
            title="View all sections"
          >
            <List className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        {/* Interview mode badge */}
        {modeBadge && (
          <div className="mt-2 flex items-center justify-center gap-2 text-xs text-quiltt-navy bg-quiltt-50 py-1.5 px-3 rounded-full mx-auto w-fit">
            <modeBadge.icon className="w-3.5 h-3.5" />
            <span>{modeBadge.label}</span>
          </div>
        )}
      </header>
      
      {/* Question with navigation arrows */}
      <div className="flex-1 flex items-center overflow-hidden relative">
        {/* Left arrow */}
        <button
          onClick={handleNavigatePrevious}
          disabled={isRecording || showPreview}
          className="absolute left-2 z-10 p-2 rounded-full bg-white/80 shadow-md hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
        >
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        
        {/* Question content */}
        <div className="flex-1 flex items-center justify-center p-6 px-12">
          <div className="max-w-2xl w-full text-center">
            {/* Answered badge */}
            {existingResponse && !isAddingMore && (
              <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-medium px-3 py-1 rounded-full mb-3">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Answered
              </div>
            )}
            
            <p className="text-xs font-semibold text-quiltt-navy uppercase tracking-wider mb-2">
              {currentQuestion.section.replace('_', ' ')}
            </p>
            
            <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 leading-snug mb-4">
              {lifeStory?.storyType === 'couple'
                ? currentQuestion.question.replace('{name}', getDisplayName())
                : currentQuestion.question.replace('{name}', residentName)
              }
            </h2>
            
            {currentQuestion.followUp && !existingResponse && (
              <p className="text-lg text-gray-500 italic mb-4">
                {currentQuestion.followUp}
              </p>
            )}
            
            {/* Mode-specific tip - only show when recording new */}
            {modeBadge?.tip && !existingResponse && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700 text-left">
                💡 {modeBadge.tip}
              </div>
            )}
          </div>
        </div>
        
        {/* Right arrow */}
        <button
          onClick={handleNavigateNext}
          disabled={isRecording || showPreview}
          className="absolute right-2 z-10 p-2 rounded-full bg-white/80 shadow-md hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
        >
          <ChevronRight className="w-6 h-6 text-gray-600" />
        </button>
      </div>
      
      {/* Controls */}
      <div className="bg-white border-t p-4 pb-6 md:p-6 flex-shrink-0 safe-area-bottom">
        <div className="max-w-md mx-auto">
          {recorderError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {recorderError}
            </div>
          )}
          
          {saveError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {saveError}
            </div>
          )}
          
          {/* Show existing recording controls */}
          {existingResponse && !isAddingMore ? (
            <ExistingRecordingControls
              playback={existingPlayback}
              audioUrl={existingAudioUrl}
              loading={loadingExisting}
              onAddMore={handleAddMore}
              onDelete={handleDelete}
              onContinue={handleNavigateNext}
              isDeleting={isDeleting}
              additionalCount={existingResponse.additionalRecordings?.length || 0}
            />
          ) : showPreview ? (
            /* Show preview of new recording */
            <PreviewControls
              duration={duration}
              playback={playback}
              onSave={isAddingMore ? handleSaveAdditional : handleSave}
              onReRecord={isAddingMore ? handleCancelAddMore : handleReRecord}
              isSaving={isSaving}
              isAddingMore={isAddingMore}
            />
          ) : (
            /* Show record button */
            <div className="flex items-center justify-between">
              {isAddingMore ? (
                <button
                  onClick={handleCancelAddMore}
                  disabled={isRecording}
                  className="flex flex-col items-center p-3 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  <ArrowLeft className="w-6 h-6" />
                  <span className="text-xs mt-1">Cancel</span>
                </button>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={handleSkip}
                    disabled={isRecording}
                    className="flex flex-col items-center p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  >
                    <SkipForward className="w-5 h-5" />
                    <span className="text-xs">Skip</span>
                  </button>
                  {canSkipSection && (
                    <button
                      onClick={handleSkipSection}
                      disabled={isRecording}
                      className="flex flex-col items-center p-1 text-gray-400 hover:text-orange-600 disabled:opacity-50 text-xs"
                    >
                      <FastForward className="w-4 h-4" />
                      <span>Skip Section</span>
                    </button>
                  )}
                </div>
              )}
              
              <RecordButton
                isRecording={isRecording}
                duration={duration}
                onClick={handleRecord}
              />
              
              <div className="w-16" /> {/* Spacer for centering */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================
// Sub-components
// ============================================

function RecordButton({ isRecording, duration, onClick, error }) {
  const formatDuration = (seconds) => {
    // Handle invalid values (Infinity, NaN)
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '--:--';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <button
      onClick={onClick}
      className={`
        relative w-24 h-24 md:w-28 md:h-28 rounded-full 
        flex flex-col items-center justify-center
        shadow-lg transition-all
        ${isRecording 
          ? 'bg-red-600 hover:bg-red-700 recording-pulse' 
          : 'bg-red-500 hover:bg-red-600'
        }
      `}
    >
      {isRecording ? (
        <>
          <Square className="w-8 h-8 text-white fill-current" />
          <span className="text-white text-sm font-medium mt-1">
            {formatDuration(duration)}
          </span>
        </>
      ) : (
        <>
          <Mic className="w-10 h-10 text-white" />
          <span className="text-white text-xs mt-1">Tap to Record</span>
        </>
      )}
    </button>
  );
}

function PreviewControls({ duration, playback, onSave, onReRecord, isSaving, isAddingMore }) {
  const formatDuration = (seconds) => {
    // Handle invalid values (Infinity, NaN)
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '--:--';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="text-center">
      <p className="text-sm text-gray-500 mb-1">
        {isAddingMore ? 'Additional Recording' : 'Preview Recording'}
      </p>
      <p className="text-2xl font-semibold text-gray-900 mb-4">
        {formatDuration(duration)}
      </p>
      
      <button
        onClick={playback.togglePlayPause}
        className="w-14 h-14 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6"
      >
        {playback.isPlaying ? (
          <Pause className="w-6 h-6 text-quiltt-navy" />
        ) : (
          <Play className="w-6 h-6 text-quiltt-navy ml-1" />
        )}
      </button>
      
      <div className="flex gap-3 justify-center">
        <button
          onClick={onReRecord}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium disabled:opacity-50"
        >
          <RotateCcw className="w-4 h-4" />
          {isAddingMore ? 'Cancel' : 'Re-record'}
        </button>
        
        <button
          onClick={onSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-3 bg-quiltt-navy hover:bg-quiltt-800 rounded-lg text-white font-medium disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              {isAddingMore ? 'Add Recording' : 'Save & Continue'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function ExistingRecordingControls({ playback, audioUrl, loading, onAddMore, onDelete, onContinue, isDeleting, additionalCount }) {
  const formatDuration = (seconds) => {
    // Handle invalid values (Infinity, NaN)
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '--:--';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="w-6 h-6 border-2 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-gray-500 mt-2">Loading recording...</p>
      </div>
    );
  }
  
  return (
    <div className="text-center">
      <p className="text-sm text-gray-500 mb-1">
        Your Recording {additionalCount > 0 && `(+${additionalCount} more)`}
      </p>
      
      {/* Playback button */}
      <button
        onClick={playback.togglePlayPause}
        disabled={!audioUrl}
        className="w-16 h-16 bg-quiltt-purple/10 hover:bg-quiltt-purple/20 rounded-full flex items-center justify-center mx-auto mb-4 disabled:opacity-50 transition-colors"
      >
        {playback.isPlaying ? (
          <Pause className="w-7 h-7 text-quiltt-purple" />
        ) : (
          <Play className="w-7 h-7 text-quiltt-purple ml-1" />
        )}
      </button>
      
      {/* Progress bar */}
      {isFinite(playback.duration) && playback.duration > 0 && (
        <div className="mb-4">
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-1">
            <div 
              className="h-full bg-quiltt-purple transition-all duration-100"
              style={{ width: `${(playback.currentTime / playback.duration) * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-500">
            {formatDuration(playback.currentTime)} / {formatDuration(playback.duration)}
          </p>
        </div>
      )}
      
      {/* Action buttons */}
      <div className="flex gap-3 justify-center">
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 font-medium disabled:opacity-50 transition-colors"
        >
          {isDeleting ? (
            <>
              <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
              Deleting...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4" />
              Delete
            </>
          )}
        </button>
        
        <button
          onClick={onAddMore}
          className="flex items-center gap-2 px-4 py-2.5 bg-quiltt-purple hover:bg-purple-700 rounded-lg text-white font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add More
        </button>
      </div>
      
      {/* Continue button */}
      <button
        onClick={onContinue}
        className="mt-3 w-full flex items-center justify-center gap-2 px-6 py-3 bg-quiltt-navy hover:bg-quiltt-800 rounded-lg text-white font-medium transition-colors"
      >
        Continue
        <ChevronRight className="w-4 h-4" />
      </button>
      
      <p className="text-xs text-gray-400 mt-2 text-center">
        Or swipe left/right to navigate
      </p>
    </div>
  );
}
